import styles from '../Columns/columns.module.scss';
import { RESIZABLE_COLUMNS } from './resizableConfig';

type ColumnWidths = Record<string, number>;

/**
 * Get the CSS variable name for a column key
 * e.g., 'title' -> 'column_title_width'
 */
const getColumnCssKey = (columnKey: string): string => {
  return `column_${columnKey}_width`;
};

/**
 * Calculate base table width (includes all NON-resizable column widths)
 * When resizable is enabled, resizable columns are excluded from base width
 * because they're calculated separately in getResizableColumnsWidth()
 */
const getBaseTableWidth = (isResizableEnabled: boolean): number => {
  // Get set of resizable column CSS keys for quick lookup
  const resizableColumnKeys = new Set(
    RESIZABLE_COLUMNS.map((col) => getColumnCssKey(col.key))
  );

  return Object.entries(styles)
    .filter(([key]) => key.startsWith('column_') && key.endsWith('_width'))
    .filter(([key]) => {
      // When resizable is enabled, exclude resizable columns from base width
      if (isResizableEnabled && resizableColumnKeys.has(key)) {
        return false;
      }
      return true;
    })
    .map(([, value]) => Number(value))
    .reduce((sum, width) => sum + width, 0);
};

/**
 * Calculate total width of resizable columns based on current widths
 */
const getResizableColumnsWidth = (
  columnWidths: ColumnWidths,
  isResizableEnabled: boolean
): number => {
  if (!isResizableEnabled) return 0;

  return RESIZABLE_COLUMNS.reduce((sum, col) => {
    const width = columnWidths[col.key] ?? col.defaultWidth;
    return sum + width;
  }, 0);
};

/**
 * Calculate total table width
 * Base width + resizable columns width
 */
export const getTotalTableWidth = (
  columnWidths: ColumnWidths,
  isResizableEnabled: boolean
): number => {
  const baseWidth = getBaseTableWidth(isResizableEnabled);
  const resizableWidth = getResizableColumnsWidth(
    columnWidths,
    isResizableEnabled
  );
  return baseWidth + resizableWidth;
};
